import React from "react";

/**
 * Reusable background wrapper with separate desktop vs mobile/tablet behavior.
 *
 * Desktop:
 *  - backgroundSize: 120% (zoom effect)
 *  - backgroundAttachment: fixed (parallax effect)
 *  - backgroundPosition: center
 *
 * Tablet:
 *  - backgroundSize: 115% (slightly zoomed out)
 *  - backgroundAttachment: scroll
 *
 * Mobile:
 *  - backgroundSize: 110% (more zoomed out than tablet)
 *  - backgroundAttachment: scroll
 *
 * Props:
 * - desktopImage (required): imported image (string URL)
 * - mobileImage (optional): mobile/tablet image; falls back to desktopImage
 * - overlayClass (optional): Tailwind classes for overlay (e.g. "bg-black/40")
 */

const BackgroundWrapper = ({
  desktopImage,
  mobileImage,
  children,
  overlayClass = "",
  desktopSize = "120%",
  desktopPosition = "center",
  desktopAttachment = "fixed",
  mobilePosition = "center",
}) => {
  const mobileSrc = mobileImage || desktopImage;

  return (
    <div className="min-h-screen">
      {/* Desktop / Laptop (unchanged) */}
      <div
        className="hidden xl:block min-h-screen bg-no-repeat"
        style={{
          backgroundImage: `url(${desktopImage})`,
          backgroundSize: desktopSize, // 120%
          backgroundPosition: desktopPosition,
          backgroundAttachment: desktopAttachment,
        }}
      >
        <div className="relative min-h-screen">
          {overlayClass && <div className={`absolute inset-0 ${overlayClass}`} />}
          <div className="relative z-10">{children}</div>
        </div>
      </div>

      {/* Tablet (md → xl) */}
      <div
        className="hidden md:block xl:hidden min-h-screen bg-no-repeat"
        style={{
          backgroundImage: `url(${mobileSrc})`,
          backgroundSize: "388%",          // 👈 zoomed out a bit
          backgroundPosition: mobilePosition,
          backgroundAttachment: "scroll",
        }}
      >
        <div className="relative min-h-screen">
          {overlayClass && <div className={`absolute inset-0 ${overlayClass}`} />}
          <div className="relative z-10">{children}</div>
        </div>
      </div>

      {/* Mobile (< md) */}
      <div
        className="block md:hidden min-h-screen bg-no-repeat"
        style={{
          backgroundImage: `url(${mobileSrc})`,
          backgroundSize: "1680%",          // 👈 more zoomed out than tablet
          backgroundPosition: mobilePosition,
          backgroundAttachment: "scroll",
        }}
      >
        <div className="relative min-h-screen">
          {overlayClass && <div className={`absolute inset-0 ${overlayClass}`} />}
          <div className="relative z-10">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundWrapper;
